import React, { useState, useEffect } from 'react';
import type { TOptions } from 'i18next';
import { Container, Row, Col, Card, ProgressBar, Button, Navbar, Nav, Modal, Badge, Spinner, Tab, Tabs, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useClientXpBoostCountdown } from '../hooks/useClientXpBoostCountdown';
import {
  User,
  ExperienceInfo,
  RankingEntry,
  AchievementRankingEntry,
  DailyMissionsToday,
  authService,
  achievementService,
  friendshipService,
  dailyMissionService,
  loginStreakService,
  loadUnreadNotificationCount,
  type LoginStreakHome,
} from '../services/api';
import QuizList from './QuizList';
import PvPLobby from './PvPLobby';
import CareerModeTab from './CareerModeTab';
import LoginPage from './LoginPage';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import LivesDisplay from './LivesDisplay';
import PulseBadge from './shared/PulseBadge';
import './HomePage.css';
import DailyMissionsSidebarCard from './DailyMissionsSidebarCard';
import LoginStreakSidebarCard from './LoginStreakSidebarCard';

const HomePage: React.FC = () => {
  const { t, ready, i18n } = useSafeTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [experienceInfo, setExperienceInfo] = useState<ExperienceInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [achievementRanking, setAchievementRanking] = useState<AchievementRankingEntry[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingPlayerError, setRankingPlayerError] = useState(false);
  const [rankingAchievementError, setRankingAchievementError] = useState(false);
  const [dailyMissions, setDailyMissions] = useState<DailyMissionsToday | null>(null);
  const [dailyMissionsLoading, setDailyMissionsLoading] = useState(false);
  const [dailyMissionsError, setDailyMissionsError] = useState(false);
  const [loginStreak, setLoginStreak] = useState<LoginStreakHome | null>(null);
  const [loginStreakLoading, setLoginStreakLoading] = useState(false);
  const [loginStreakError, setLoginStreakError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromSearch = (tab: string | null): 'solo' | 'pvp' | 'career' => {
    if (tab === 'pvp') return 'pvp';
    if (tab === 'career') return 'career';
    return 'solo';
  };
  const [activeTab, setActiveTab] = useState<'solo' | 'pvp' | 'career'>(() =>
    tabFromSearch(searchParams.get('tab'))
  );
  /** Destaca o cartão do quiz na aba Solo (ex.: vindo do modo carreira). */
  const [soloFocusQuizId, setSoloFocusQuizId] = useState<string | null>(null);
  /** Abre o modal de confirmação Solo (ou bloqueado) para o quiz de origem da missão. */
  const [soloAutoConfirmQuizId, setSoloAutoConfirmQuizId] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(tabFromSearch(searchParams.get('tab')));
  }, [searchParams]);

  useEffect(() => {
    const join = searchParams.get('pvpJoin');
    if (!join?.trim()) return;
    if (searchParams.get('tab') !== 'pvp') {
      setActiveTab('pvp');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'pvp');
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const handleMainTabSelect = (k: string | null) => {
    const key = (k || 'solo') as 'solo' | 'pvp' | 'career';
    setActiveTab(key);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'pvp') next.set('tab', 'pvp');
        else if (key === 'career') next.set('tab', 'career');
        else next.delete('tab');
        return next;
      },
      { replace: true }
    );
  };

  const goToSoloTabWithQuiz = (sourceQuizId: string) => {
    setSoloFocusQuizId(sourceQuizId);
    setSoloAutoConfirmQuizId(sourceQuizId);
    setActiveTab('solo');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true }
    );
  };

  // Safe translation function with fallbacks
  const safeT = (key: string, fallback: string, options?: TOptions) => {
    if (options !== undefined) {
      return t(key, { ...options, defaultValue: fallback });
    }
    return t(key, fallback);
  };

  const pvpTabPrefix = safeT('navigation.pvpArenaPrefix', '');
  const pvpTabSuffix = safeT('navigation.pvpArenaSuffix', '');
  /** EN: "PvP Arena" — badge after "Arena". PT: "Arena PvP" — badge after "PvP". */
  const pvpTabBadgeAfterArena = i18n.language?.toLowerCase().startsWith('en');

  const xpPotionTabBadge = useClientXpBoostCountdown(user?.activeXpBoost ?? undefined);
  const xpPotionBadgeEl = xpPotionTabBadge.show ? (
    <PulseBadge
      variant="cyan"
      size="xs"
      className="home-tab-xp-potion-badge"
      title={safeT('navigation.xpPotionBadgeTitle', 'XP potion boost active')}
    >
      +{xpPotionTabBadge.bonusPercent}%
    </PulseBadge>
  ) : null;

  // Get token and userId from URL parameters
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  useEffect(() => {
    loadUserData();
    loadRanking(); // Carregar ranking para todos os usuários
  }, []);

  useEffect(() => {
    if (user) {
      loadPendingRequestsCount();
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadUserData();
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [user?.id]);

  const loadDailyMissions = async () => {
    if (!localStorage.getItem('authToken')) {
      setDailyMissions(null);
      setDailyMissionsError(false);
      setDailyMissionsLoading(false);
      return;
    }
    setDailyMissionsLoading(true);
    setDailyMissionsError(false);
    try {
      const d = await dailyMissionService.getToday();
      setDailyMissions(d);
    } catch {
      setDailyMissions(null);
      setDailyMissionsError(true);
    } finally {
      setDailyMissionsLoading(false);
    }
  };

  const loadLoginStreak = async () => {
    if (!localStorage.getItem('authToken')) {
      setLoginStreak(null);
      setLoginStreakError(false);
      setLoginStreakLoading(false);
      return;
    }
    setLoginStreakLoading(true);
    setLoginStreakError(false);
    try {
      const s = await loginStreakService.getHome();
      setLoginStreak(s);
    } catch {
      setLoginStreak(null);
      setLoginStreakError(true);
    } finally {
      setLoginStreakLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      void loadDailyMissions();
      void loadLoginStreak();
    } else {
      setDailyMissions(null);
      setDailyMissionsError(false);
      setDailyMissionsLoading(false);
      setLoginStreak(null);
      setLoginStreakError(false);
      setLoginStreakLoading(false);
    }
  }, [user?.id]);

  const loadUserData = async () => {
    // Store token in localStorage if provided
    if (token) {
      localStorage.setItem('authToken', token);
    }
    
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const [userData, experienceData] = await Promise.all([
            authService.getProfile(),
            authService.getExperienceInfo()
          ]);
          setUser(userData);
          setExperienceInfo(experienceData);
          setUnreadNotificationCount(await loadUnreadNotificationCount(userData));
          void loadDailyMissions();
          void loadLoginStreak();
          setError(null);
        } catch (error) {
          // Token is invalid, clear it
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('userId');
          setUser(null);
          setExperienceInfo(null);
          setUnreadNotificationCount(0);
          setDailyMissions(null);
          setDailyMissionsError(false);
          setLoginStreak(null);
          setLoginStreakError(false);
        }
      }
    } catch {
      setError('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    setUser(null);
    setExperienceInfo(null);
    setUnreadNotificationCount(0);
    setDailyMissions(null);
    setDailyMissionsError(false);
    setLoginStreak(null);
    setLoginStreakError(false);
    setLoginStreakLoading(false);
    window.location.href = '/';
  };

  const handleStartQuiz = () => {
    if (!user) {
      setShowLoginModal(true);
    }
    // If user is logged in, the QuizList component will handle navigation
  };

  const loadPendingRequestsCount = async () => {
    try {
      const requests = await friendshipService.getPendingRequests();
      setPendingRequestsCount(requests.length);
    } catch {
      /* ignore */
    }
  };

  const loadRanking = async () => {
    setRankingLoading(true);
    setRankingPlayerError(false);
    setRankingAchievementError(false);
    try {
      const [userRes, achievementRes] = await Promise.allSettled([
        authService.getRanking(),
        achievementService.getRanking(1, 5),
      ]);
      if (userRes.status === 'fulfilled') {
        setRanking(userRes.value);
      } else {
        setRanking([]);
        setRankingPlayerError(true);
      }
      if (achievementRes.status === 'fulfilled') {
        setAchievementRanking(achievementRes.value.items);
      } else {
        setAchievementRanking([]);
        setRankingAchievementError(true);
      }
    } finally {
      setRankingLoading(false);
    }
  };

  const rankingRowLabel = (name: string, rank: number) =>
    safeT('ranking.viewPlayerProfile', 'View profile of {{name}}, rank {{rank}}')
      .replace('{{name}}', name)
      .replace('{{rank}}', String(rank));

  const openProfile = (id: string) => {
    window.location.href = `/profile/${id}`;
  };

  const rankingRowKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openProfile(id);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <i className="bi bi-trophy-fill text-warning"></i>;
      case 2:
        return <i className="bi bi-award-fill text-secondary"></i>;
      case 3:
        return <i className="bi bi-award-fill text-bronze"></i>;
      default:
        return <span className="rank-number-sidebar">{rank}</span>;
    }
  };

  const getRankBadgeVariant = (rank: number) => {
    switch (rank) {
      case 1:
        return 'warning';
      case 2:
        return 'secondary';
      case 3:
        return 'danger';
      default:
        return 'primary';
    }
  };

  const handlePlayerClick = (uid: string) => {
    openProfile(uid);
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    loadUserData(); // Reload user data after successful login
  };

  const handleRequestResponded = () => {
    loadPendingRequestsCount();
  };

  if (isLoading || !ready) {
    return (
      <div className="home-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Navbar */}
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            className="navbar-brand d-flex align-items-center"
            onClick={() => (window.location.href = '/')}
            style={{ cursor: 'pointer' }}
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            {user && (
              <NotificationBell
                className="d-lg-none"
                unreadCount={unreadNotificationCount}
                onUnreadCountChange={setUnreadNotificationCount}
              />
            )}
            {user ? (
              <Navbar.Toggle aria-controls="basic-navbar-nav" />
            ) : (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 home-navbar-login-icon"
                onClick={() => setShowLoginModal(true)}
                title={safeT('common.login', 'Login')}
                aria-label={safeT('common.login', 'Login')}
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
              </Button>
            )}
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              {user ? (
                <UserNavbarMenus
                  user={user}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Nav.Item className="d-none d-lg-block">
                  <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    {safeT('common.login', 'Login')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {user ? (
        <h1 className="visually-hidden">
          {safeT(
            'seo.home.homeLoggedH1',
            'Quizter Club — your quizzes, leaderboard and PvP arena'
          )}
        </h1>
      ) : null}

      {/* Logged-in: compact stats under navbar on small screens only */}
      {user && (
        <div className="d-lg-none home-user-stats-mobile">
          <Container fluid className="px-3">
            <Card className="stats-card-sidebar stats-card-mobile-compact">
              <Card.Body className="py-2 px-3">
                <div className="user-stats-mobile-row">
                  <div className="user-stats-mobile-stat">
                    <span className="user-stats-mobile-value">{user.level}</span>
                    <span className="user-stats-mobile-label">{safeT('user.level', 'Level')}</span>
                  </div>
                  <div className="user-stats-mobile-stat">
                    <span className="user-stats-mobile-value">{user.experiencePoints}</span>
                    <span className="user-stats-mobile-label">XP</span>
                  </div>
                  {experienceInfo && (
                    <div className="user-stats-mobile-xp">
                      <ProgressBar
                        now={experienceInfo.xpProgress}
                        className="xp-progress-bar-sidebar user-stats-mobile-progress"
                        variant="success"
                      />
                      <small className="text-muted user-stats-mobile-pct">
                        {experienceInfo.xpProgress}%
                      </small>
                    </div>
                  )}
                </div>
                <div className="user-stats-mobile-lives-streak-row">
                  <div className="user-stats-mobile-lives-col">
                    <LivesDisplay
                      key={`${user.userType}-${user.lives}-${user.livesStatus?.nextRegenerationAt ?? 'none'}-${user.livesStatus?.serverTimeUtc ?? ''}`}
                      lives={user.lives}
                      livesStatus={user.livesStatus}
                      userType={user.userType}
                      onCountdownElapsed={() => {
                        void loadUserData();
                      }}
                    />
                  </div>
                  <div className="user-stats-mobile-streak-divider" aria-hidden />
                  <div className="user-stats-mobile-streak-col">
                    <LoginStreakSidebarCard
                      variant="mobile"
                      data={loginStreak}
                      loading={loginStreakLoading}
                      error={loginStreakError}
                      onRetry={() => void loadLoginStreak()}
                      safeT={safeT}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
            <DailyMissionsSidebarCard
              variant="mobile"
              data={dailyMissions}
              loading={dailyMissionsLoading}
              error={dailyMissionsError}
              onRetry={() => void loadDailyMissions()}
              safeT={safeT}
              itemName={(key) => safeT(`inventory.items.${key}.name`, key)}
              itemShortName={(key) => safeT(`inventory.items.${key}.short`, key)}
            />
          </Container>
        </div>
      )}

      {/* Main Content */}
      <Container fluid className="home-content">
        {!user ? (
          // Hero Section for non-logged users
          <Row className="mb-3">
            <Col>
              <div className="hero-section text-center">
                <h1 className="hero-title">{safeT('common.welcome', 'Welcome to Quizter Club')}</h1>
                <p className="hero-subtitle">{safeT('common.welcomeSubtitle', 'Learn and have fun with interactive quizzes!')}</p>
              </div>
            </Col>
          </Row>
        ) : null}

        {/* Main Content Section - New Layout */}
        <Row className="main-content-row">
          {/* Quiz List Section - 80% width */}
          <Col lg={10} className="quiz-section">
            {/* className on Tabs only targets the <ul>; wrapper groups <ul> + .tab-content for layout CSS */}
            <div className="main-tabs">
              <Tabs
                activeKey={activeTab}
                onSelect={handleMainTabSelect}
              >
              <Tab
                eventKey="solo"
                title={
                  <span className="d-inline-flex align-items-center">
                    <i className="bi bi-person-fill me-2" aria-hidden />
                    <span className="home-tab-title-anchor">
                      {safeT('navigation.soloQuizzes', 'Quizzes')}
                      {xpPotionBadgeEl ? <span className="home-tab-nav-badges-clip">{xpPotionBadgeEl}</span> : null}
                    </span>
                  </span>
                }
              >
                <QuizList
                  isUserLoggedIn={!!user}
                  user={user}
                  onLoginSuccess={handleLoginSuccess}
                  focusQuizId={soloFocusQuizId}
                  onFocusQuizConsumed={() => setSoloFocusQuizId(null)}
                  autoOpenSoloConfirmQuizId={soloAutoConfirmQuizId}
                  onAutoOpenSoloConfirmConsumed={() => setSoloAutoConfirmQuizId(null)}
                  mainTab={activeTab}
                />
              </Tab>
              
              <Tab
                eventKey="pvp"
                tabClassName="home-tab-nav--pvp"
                title={
                  <span className="home-tab-pvp-title d-inline-flex align-items-center">
                    <i className="bi bi-people-fill me-2 flex-shrink-0" aria-hidden />
                    <span className="home-tab-pvp-label">
                      {pvpTabBadgeAfterArena ? (
                        <>
                          PvP
                          {pvpTabSuffix ? (
                            <>
                              {' '}
                              <span className="home-tab-pvp-anchor">
                                {pvpTabSuffix}
                                <span className="home-tab-nav-badges-clip">
                                  {xpPotionBadgeEl}
                                  <PulseBadge variant="orange" size="xs" className="home-tab-pvp-xp2-badge">
                                    {safeT('navigation.pvpXp2Badge', 'XP 2x')}
                                  </PulseBadge>
                                </span>
                              </span>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {pvpTabPrefix}
                          {pvpTabPrefix ? ' ' : ''}
                          <span className="home-tab-pvp-anchor">
                            PvP
                            <span className="home-tab-nav-badges-clip">
                              {xpPotionBadgeEl}
                              <PulseBadge variant="orange" size="xs" className="home-tab-pvp-xp2-badge">
                                {safeT('navigation.pvpXp2Badge', 'XP 2x')}
                              </PulseBadge>
                            </span>
                          </span>
                          {pvpTabSuffix ? ` ${pvpTabSuffix}` : ''}
                        </>
                      )}
                    </span>
                  </span>
                }
              >
                <PvPLobby
                  isUserLoggedIn={!!user}
                  onLoginSuccess={handleLoginSuccess}
                  pvpJoinCode={searchParams.get('pvpJoin')}
                  onPvpJoinConsumed={() => {
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete('pvpJoin');
                        return next;
                      },
                      { replace: true }
                    );
                  }}
                />
              </Tab>

              <Tab
                eventKey="career"
                title={
                  <span className="d-inline-flex align-items-center">
                    <i className="bi bi-flag-fill me-2" aria-hidden />
                    <span className="home-tab-title-anchor">
                      {safeT('navigation.careerMode', 'Career')}
                      {xpPotionBadgeEl ? <span className="home-tab-nav-badges-clip">{xpPotionBadgeEl}</span> : null}
                    </span>
                  </span>
                }
              >
                <CareerModeTab
                  isUserLoggedIn={!!user}
                  onGoToSoloTab={goToSoloTabWithQuiz}
                />
              </Tab>
              </Tabs>
            </div>
          </Col>
          
          {/* Right Sidebar - desktop only (stats + rankings); mobile stats live under navbar */}
          <Col lg={2} className="sidebar-section d-none d-lg-flex flex-column">
            {user && (
              <>
                {/* User Stats Card */}
                <div className="user-stats-sidebar">
                  <Card className="stats-card-sidebar">
                    <Card.Body className="p-1">
                      <div className="user-stats-sidebar-level-xp-row">
                        <div className="stat-item-sidebar">
                          <div className="stat-value-sidebar">{user.level}</div>
                          <div className="stat-label-sidebar">{safeT('user.level', 'Level')}</div>
                        </div>
                        <div className="stat-item-sidebar">
                          <div className="stat-value-sidebar">{user.experiencePoints}</div>
                          <div className="stat-label-sidebar">XP</div>
                        </div>
                      </div>

                      {experienceInfo && (
                        <div className="xp-progress-container-sidebar">
                          <ProgressBar 
                            now={experienceInfo.xpProgress} 
                            className="xp-progress-bar-sidebar"
                            variant="success"
                          />
                          <small className="text-muted d-block text-center">
                            {experienceInfo.xpProgress}%
                          </small>
                        </div>
                      )}
                      
                      <div className="lives-section-sidebar">
                        <LivesDisplay
                          key={`${user.userType}-${user.lives}-${user.livesStatus?.nextRegenerationAt ?? 'none'}-${user.livesStatus?.serverTimeUtc ?? ''}`}
                          lives={user.lives}
                          livesStatus={user.livesStatus}
                          userType={user.userType}
                          onCountdownElapsed={() => {
                            void loadUserData();
                          }}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <LoginStreakSidebarCard
                  data={loginStreak}
                  loading={loginStreakLoading}
                  error={loginStreakError}
                  onRetry={() => void loadLoginStreak()}
                  safeT={safeT}
                />
                <DailyMissionsSidebarCard
                  variant="desktop"
                  data={dailyMissions}
                  loading={dailyMissionsLoading}
                  error={dailyMissionsError}
                  onRetry={() => void loadDailyMissions()}
                  safeT={safeT}
                  itemName={(key) => safeT(`inventory.items.${key}.name`, key)}
                  itemShortName={(key) => safeT(`inventory.items.${key}.short`, key)}
                />
              </>
            )}
            
            {/* Top Players Card - desktop sidebar only (hidden on mobile) */}
            <div className="top-players-sidebar d-none d-lg-block">
              <Card
                className="top-players-card-sidebar"
                aria-busy={rankingLoading}
                aria-label={safeT('ranking.topPlayers', 'Top Players')}
              >
                <Card.Header className="p-2 text-center">
                  <small className="fw-bold">{safeT('ranking.topPlayers', 'Top Players')}</small>
                </Card.Header>
                <Card.Body className="p-2">
                  {rankingLoading ? (
                    <div className="text-center" role="status" aria-live="polite">
                      <Spinner animation="border" size="sm">
                        <span className="visually-hidden">
                          {safeT('ranking.sidebarLoadingRankings', 'Loading rankings')}
                        </span>
                      </Spinner>
                    </div>
                  ) : rankingPlayerError ? (
                    <div className="px-1">
                      <Alert variant="warning" className="small py-2 mb-2">
                        {safeT('ranking.loadErrorPlayers', 'Could not load player ranking.')}
                      </Alert>
                      <Button variant="link" size="sm" className="p-0 small" onClick={loadRanking}>
                        {safeT('ranking.retry', 'Try again')}
                      </Button>
                    </div>
                  ) : ranking.length === 0 ? (
                    <div className="ranking-empty-state">
                      <small>{safeT('ranking.noPlayers', 'No players')}</small>
                    </div>
                  ) : (
                    <div className="ranking-list-sidebar">
                      {ranking.slice(0, 5).map((player) => (
                        <div 
                          key={player.id} 
                          className="ranking-item-sidebar"
                          role="button"
                          tabIndex={0}
                          onClick={() => handlePlayerClick(player.id)}
                          onKeyDown={(e) => rankingRowKeyDown(e, player.id)}
                          style={{ cursor: 'pointer' }}
                          aria-label={rankingRowLabel(player.nickname, player.rank)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="rank-icon-sidebar me-2">
                                {getRankIcon(player.rank)}
                              </div>
                              <div className="player-info-sidebar">
                                <div
                                  className={`player-nickname-sidebar${
                                    player.isVip === true ? ' vip-subscriber-nickname' : ''
                                  }`}
                                >
                                  {player.nickname}
                                  {player.isVip === true && (
                                    <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                                  )}
                                </div>
                                <div className="player-level-sidebar">
                                  <Badge bg="info" className="me-1">
                                    {safeT('user.level', 'Level')} {player.level}
                                  </Badge>
                                  <small className="text-muted">
                                    {player.experiencePoints.toLocaleString()} XP
                                  </small>
                                </div>
                              </div>
                            </div>
                            <Badge bg={getRankBadgeVariant(player.rank)} className="rank-badge-sidebar">
                              #{player.rank}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>

            <div className="top-players-sidebar d-none d-lg-block">
              <Card
                className="top-players-card-sidebar"
                aria-busy={rankingLoading}
                aria-label={safeT('ranking.topAchievements', 'Top achievements')}
              >
                <Card.Header className="p-2 text-center">
                  <small className="fw-bold">
                    {safeT('ranking.topAchievements', 'Top achievements')}
                  </small>
                </Card.Header>
                <Card.Body className="p-2">
                  {rankingLoading ? (
                    <div className="text-center" role="status" aria-live="polite">
                      <Spinner animation="border" size="sm">
                        <span className="visually-hidden">
                          {safeT('ranking.sidebarLoadingRankings', 'Loading rankings')}
                        </span>
                      </Spinner>
                    </div>
                  ) : rankingAchievementError ? (
                    <div className="px-1">
                      <Alert variant="warning" className="small py-2 mb-2">
                        {safeT('ranking.loadErrorAchievements', 'Could not load achievement ranking.')}
                      </Alert>
                      <Button variant="link" size="sm" className="p-0 small" onClick={loadRanking}>
                        {safeT('ranking.retry', 'Try again')}
                      </Button>
                    </div>
                  ) : achievementRanking.length === 0 ? (
                    <div className="ranking-empty-state">
                      <small>{safeT('ranking.noAchievements', 'No data')}</small>
                    </div>
                  ) : (
                    <div className="ranking-list-sidebar">
                      {achievementRanking.map((player) => (
                        <div
                          key={player.userId}
                          className="ranking-item-sidebar"
                          role="button"
                          tabIndex={0}
                          onClick={() => handlePlayerClick(player.userId)}
                          onKeyDown={(e) => rankingRowKeyDown(e, player.userId)}
                          style={{ cursor: 'pointer' }}
                          aria-label={rankingRowLabel(player.nickname, player.rank)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="rank-icon-sidebar me-2">
                                {getRankIcon(player.rank)}
                              </div>
                              <div className="player-info-sidebar">
                                <div
                                  className={`player-nickname-sidebar${
                                    player.isVip === true ? ' vip-subscriber-nickname' : ''
                                  }`}
                                >
                                  {player.nickname}
                                  {player.isVip === true && (
                                    <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                                  )}
                                </div>
                                <div className="player-level-sidebar">
                                  <Badge bg="info" className="me-1">
                                    {safeT('user.level', 'Level')} {player.level}
                                  </Badge>
                                  <small className="text-muted">
                                    {player.achievementScore.toLocaleString()}{' '}
                                    {safeT('ranking.achievementPoints', 'pts')}
                                  </small>
                                </div>
                              </div>
                            </div>
                            <Badge bg={getRankBadgeVariant(player.rank)} className="rank-badge-sidebar">
                              #{player.rank}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Login Modal */}
      <Modal 
        show={showLoginModal} 
        onHide={() => setShowLoginModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{safeT('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage onLoginSuccess={handleLoginSuccess} />
        </Modal.Body>
      </Modal>

      {/* Friends List Modal */}
      {showFriendsList && (
        <FriendsList onClose={() => setShowFriendsList(false)} />
      )}

      {/* Pending Requests Modal */}
      {showPendingRequests && (
        <PendingRequests 
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={handleRequestResponded}
        />
      )}
    </div>
  );
};

export default HomePage; 